import FileExchangePanel from '@/components/FileExchangePanel';
import { useI18n } from '@/i18n/I18nContext';
import { Skeleton, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable, { amountCell, amountHeader, dateCell } from 'src/components/DataTable';
import type { DataTableColumnDef as ColumnDef, DataTableCellContext as CellContext, DataTableRow as Row } from 'src/components/DataTable';
import { FileDownload, FileSelect } from 'src/components/FileSelect';
import { fundMoveService } from 'src/service/FundMoveService';
import { FundMove } from 'src/api/FundMove';
import Content from 'src/components/Content';
import ReachLink from 'src/components/ReachLink';
import { formatPicker } from 'src/data/DateTimeUtil';
import WaitingSpinner from '@/components/WaitingSpinner';
import AddButton from '@/components/buttons/AddButton';
import { DropDownMenu } from '@/components/DropDownMenu';
import IdAndNameData from 'src/data/IdAndNameData';
import ConfirmDialog from '@/components/ConfirmDialog';
import { useNavigate } from 'react-router-dom';

const OverviewFundsMoves = () => {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [data, setData] = useState<FundMove[]>([]);
    const [projects, setProjects] = useState<Map<number, string>>(new Map()); // id to project name
    const [deleteData, setDeleteData] = useState<IdAndNameData<number> | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    const loadAllData = useCallback((): Promise<void> => {
        setIsLoading(true);
        return fundMoveService.listAll()
            .then(r => {
                setData(r.results.concat());
                setProjects(new Map(r.projects.map(p => [p.id, p.name])));
            })
            .finally(() => setIsLoading(false));
    }, []);

    const onUpload = useCallback((): Promise<void> => {
        console.info(`upload funds suceeded...`);
        return loadAllData();
    }, [loadAllData]);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    const columns: ColumnDef<FundMove>[] = useMemo(
        () => [
            {
                id: 'transferDate',
                header: 'Datum',
                accessorKey: 'transferDate',
                cell: dateCell()
            },
            {
                id: 'fromProject',
                header: 'Von Projekt',
                accessorKey: 'fromProjectId',
                cell: (info: CellContext<FundMove, number>) => {
                    let pid = info.getValue();
                    let projectName = projects.get(pid);
                    return (<ReachLink to={`/projects/view/${pid}`} name={projectName} tooltip='zu den Projektdetails' />);
                },
                sortFn: (rowA: Row<FundMove>, rowB: Row<FundMove>, _): number => {
                    const a = rowA.original;
                    const b = rowB.original;
                    let ap = projects.get(a.fromProjectId) ?? '';
                    let bp = projects.get(b.fromProjectId) ?? '';
                    return ap.trim().localeCompare(bp.trim());
                },
            },
            {
                id: 'fromDonationCode',
                header: 'Von Kostenstelle',
                accessorKey: 'fromDonationCode'
            },
            {
                id: 'budgetYear',
                header: 'Fonds Jahr',
                accessorKey: 'budgetYear'
            },
            {
                id: 'toProject',
                header: 'Auf Projekt',
                accessorKey: 'toProjectId',
                cell: (info) => {
                    let pid = info.row.original.toProjectId;
                    let projectName = projects.get(pid);
                    return (<ReachLink to={`/projects/view/${pid}`} name={projectName} tooltip='zu den Projektdetails' />);
                },
                sortFn: (rowA: Row<FundMove>, rowB: Row<FundMove>, _): number => {
                    const a = rowA.original;
                    const b = rowB.original;
                    let ap = projects.get(a.toProjectId) ?? '';
                    let bp = projects.get(b.toProjectId) ?? '';
                    return ap.trim().localeCompare(bp.trim());
                },
            },
            {
                id: 'toDonationCode',
                header: 'Auf Kostenstelle',
                accessorKey: 'toDonationCode'
            },
            {
                id: 'fundSourceName',
                header: 'Fonds Quelle',
                accessorKey: 'fundSourceName',
                cell: (info: CellContext<FundMove, string>) => (<Text fontWeight={'bold'}>{info.getValue()}</Text>)
            },
            {
                id: 'transfer',
                header: amountHeader({ name: 'Summe', width: '7rem' }),
                accessorKey: 'transfer',
                cell: amountCell({ width: '7rem' }),
            },
            {
                id: 'edit',
                enableSorting: false,
                cell: info => {
                    const row = info.row.original;
                    return <DropDownMenu
                        onMenuView={() => navigate(`/funds/moves/view/${row.id}`)}
                        onMenuEdit={() => navigate(`/funds/moves/edit/${row.id}`)}
                        onMenuDelete={() => {
                            setDeleteData({
                                id: row.id,
                                name: `${projects.get(row.toProjectId)?.trim() ?? 'n/a'} / ${row.toDonationCode}`
                            });
                            onDeleteOpen();
                        }}
                    />;
                }
            }
        ], [projects, navigate, onDeleteOpen]
    );

    return (
        <Content
            title={t('overviews.fundAllocations.title')}
            description={t('overviews.fundAllocations.description')}
            actions={<>
                <FileExchangePanel>
                    <FileSelect path='/api/funds/moves/upload' onUpload={onUpload} />
                    <FileDownload scope='all' path='/api/funds/moves/download' name={`fondsanderungen-${formatPicker(new Date())}.xlsx`} />
                </FileExchangePanel>
                <AddButton caption={t('overviews.fundAllocations.create')} onClick={() => navigate('/funds/moves/add')} />
            </>}
        >
            <Stack gap='6'>
                {isLoading ? (
                    <Stack py={5}>
                        <WaitingSpinner size='xl' />
                        <Skeleton height='20px' />
                        <Skeleton height='20px' />
                        <Skeleton height='20px' />
                    </Stack>
                ) : (
                    <>
                        <Text fontSize='sm' color='fg.muted' textAlign='end' aria-live='polite'>{t('common.table.results', { count: data.length })}</Text>
                        <DataTable
                            columns={columns}
                            data={data}
                            initialSortByColumn='transferDate'
                            initialSortByDesc={true}
                            hideColumnsOnXs={['fromDonationCode', 'toDonationCode', 'edit']}
                            onRowClick={row => navigate(`/funds/moves/edit/${row.id}`)}
                        />
                        <ConfirmDialog
                            heading={`${deleteData?.name} Löschen`}
                            isOpen={isDeleteOpen}
                            onClose={onDeleteClose}
                            onConfirm={async (): Promise<void> => {
                                if (!deleteData) {
                                    return;
                                }
                                await fundMoveService.remove(deleteData.id);
                                await loadAllData();
                            }}
                        />
                    </>
                )}
            </Stack>
        </Content>
    );
}

export default OverviewFundsMoves
